/**
 * Sewing Efficiency — the four row chips: sample production and changeover, with and without.
 * This module is the rule; the controls live in `index.tsx`.
 *
 * **Client-side by owner ruling, and a deliberate departure from §4.3/§4.4's default.** It holds
 * only because three things are true together: membership is server-returned and never decided
 * here (§5.2); the summary result is complete; and {@link chipsAreOffered} withholds the chips
 * entirely when it is not. Selecting over a capped result would present part of the data as the
 * whole, and unlike a wrong total a wrong row set prompts no question.
 *
 * Each pair is two independent toggles: both sides lit, or neither, filters nothing, and both are
 * preserved in the URL rather than collapsed. They do not narrow the export (§5.9), which sends
 * report intent — the toolbar says so whenever a chip is active.
 */

import { z } from 'zod';

import { decodeMultiValue } from '@/lib/filters';

import type { SewingRow } from './columns';
import type { Metric } from './filters';

/** The two sides of a pair. `with` keeps the lines that qualify, `without` keeps the rest. */
const sideSchema = z.enum(['with', 'without']);
export type ChipSide = z.infer<typeof sideSchema>;

export const CHIP_SIDES: readonly ChipSide[] = ['with', 'without'];

export const SAMPLE_PARAM = 'sample';
export const CHANGEOVER_PARAM = 'changeover';

/** Which sides of each pair are lit. Order is irrelevant; membership is all that is read. */
export interface ChipSelection {
  sample: readonly ChipSide[];
  changeover: readonly ChipSide[];
}

export const NO_CHIPS: ChipSelection = { sample: [], changeover: [] };

/** Read one pair out of the URL, dropping anything that is not a side this report knows. */
export function parseChipSides(raw: string | null): ChipSide[] {
  return decodeMultiValue(raw).flatMap((token) => {
    const parsed = sideSchema.safeParse(token);
    return parsed.success ? [parsed.data] : [];
  });
}

/** Serialize one pair for the URL. Nothing selected removes the param rather than writing empty. */
export function encodeChipSides(sides: readonly ChipSide[]): string | null {
  return sides.length === 0 ? null : sides.join(',');
}

export function toggleSide(sides: readonly ChipSide[], side: ChipSide): ChipSide[] {
  return sides.includes(side) ? sides.filter((each) => each !== side) : [...sides, side];
}

/** True when any chip is lit — the toolbar says what that costs the export only when it matters. */
export function chipsAreActive(selection: ChipSelection): boolean {
  return selection.sample.length > 0 || selection.changeover.length > 0;
}

/**
 * The completeness gate (§4.3). Both halves matter: `truncated` is the server saying it stopped
 * early, and the row/total comparison catches any other way the two could disagree.
 */
export function chipsAreOffered(
  response: { rows: readonly unknown[]; total: number; truncated: boolean } | undefined,
): boolean {
  return response !== undefined && !response.truncated && response.rows.length === response.total;
}

/** The server column that says whether this metric's line ran sample production. */
function sampleField(metric: Metric): string {
  return metric === 'EINS' ? 'eins_batch_no_group' : 'sfts_batch_no_group';
}

/** Reads a server-computed column: `'SP'` is the literal `summary.sql` writes. */
function ranSampleProduction(row: SewingRow, metric: Metric): boolean {
  return row[sampleField(metric)] === 'SP';
}

function hadChangeover(row: SewingRow): boolean {
  const value = Number(row['changeover_actual_number']);
  return Number.isFinite(value) && value > 0;
}

/** One pair's verdict. Both sides lit, or neither, means the pair tests nothing. */
function pairAccepts(sides: readonly ChipSide[], qualifies: boolean): boolean {
  if (sides.length !== 1) {
    return true;
  }
  return sides[0] === 'with' ? qualifies : !qualifies;
}

/**
 * Narrow the complete summary result to the chips that are lit. The caller must have established
 * completeness first ({@link chipsAreOffered}), and must apply this once for every panel.
 */
export function applyChips(
  rows: SewingRow[],
  selection: ChipSelection,
  metric: Metric,
): SewingRow[] {
  if (!chipsAreActive(selection)) {
    return rows;
  }
  return rows.filter(
    (row) =>
      pairAccepts(selection.sample, ranSampleProduction(row, metric)) &&
      pairAccepts(selection.changeover, hadChangeover(row)),
  );
}
